import Image from 'next/image';
import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import Halftone from '@/components/agents/Halftone';
import {
  ArrowGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { formatMonth, PHOTOS, SOURCES, useAboutTranslation } from './kit';

type Milestone = {
  /** "2024-07", or just "2024" when the month isn't known. */
  date: string;
  title: string;
  text: string;
  href?: string;
  linkLabel?: string;
};

type Media =
  | {
      kind: 'photo';
      src: string;
      alt: string;
      position?: string;
      /** Landscape group shots: shown 3:2 instead of square. */
      wide?: boolean;
    }
  | { kind: 'score' };

type Chapter = {
  year: string;
  title: string;
  milestones: Milestone[];
  media?: Media[];
};

export default function AboutStory() {
  const { t } = useAboutTranslation();

  const chapters: Chapter[] = [
    {
      year: '2022',
      title: t('The first version'),
      milestones: [
        {
          date: '2022-09',
          title: t('Easy-Peasy.AI goes live'),
          text: t('A handful of AI writing tools, built by a team of three.'),
        },
      ],
    },
    {
      year: '2023',
      title: t('Becoming a company'),
      milestones: [
        {
          date: '2023-02',
          title: t('Our first interview'),
          text: t(
            'Japan’s tech magazine ASCII.jp talked to Dmytro about how Easy-Peasy.AI works.',
          ),
          href: SOURCES.ascii,
          linkLabel: t('Read on ASCII.jp'),
        },
        {
          date: '2023-08',
          title: t('Easy-Peasy.AI Pte. Ltd. is founded'),
          text: t(
            'We registered the company in Singapore as people joined from around the world.',
          ),
        },
      ],
    },
    {
      year: '2024',
      title: t('Our first awards'),
      milestones: [
        {
          date: '2024-07',
          title: t('ASEAN Startup Awards winner'),
          text: t('Best Web 3.0/AI Startup in Singapore.'),
          href: SOURCES.aseanStartupAwards,
          linkLabel: t('See the winners'),
        },
        {
          date: '2024-09',
          title: t('Global Startup Awards finalist'),
          text: t(
            'One of the global finalists at the Grand Finale in Istanbul.',
          ),
        },
        {
          date: '2024',
          title: t('Singapore SME 500 Award'),
          text: t('Named one of Singapore’s top 500 SMEs for 2024.'),
        },
      ],
      media: [
        {
          kind: 'photo',
          src: PHOTOS.aseanWinner,
          alt: t(
            'Easy-Peasy.AI, winner of Best Web 3.0/AI Startup at the ASEAN Startup Awards',
          ),
        },
        {
          kind: 'photo',
          src: PHOTOS.sme500,
          alt: t(
            'Marianna and Dmytro accepting the Singapore SME 500 Award 2024',
          ),
          position: '58% 50%',
        },
      ],
    },
    {
      year: '2025',
      title: t('Backed by Google'),
      milestones: [
        {
          date: '2025-07',
          title: t('Brands For Good Honouree'),
          text: t('Recognised in the Technology for Good category.'),
          href: SOURCES.brandsForGood,
          linkLabel: t('See the honourees'),
        },
        {
          date: '2025-10',
          title: t('Google for Startups Accelerator: AI First'),
          text: t(
            'We graduated from Google’s accelerator for AI-first startups in Singapore.',
          ),
        },
      ],
      media: [
        {
          kind: 'photo',
          src: PHOTOS.brandsForGood2025,
          alt: t(
            'Marianna and Dmytro on stage at the Brands For Good Awards 2025',
          ),
          wide: true,
        },
        {
          kind: 'photo',
          src: PHOTOS.googleDemoDay,
          alt: t(
            'Marianna and Dmytro on stage with the AI First 2025 cohort at the Google for Startups Accelerator Demo Day in Singapore',
          ),
          wide: true,
        },
      ],
    },
    {
      year: '2026',
      title: t('Trusted by the numbers'),
      milestones: [
        {
          date: '2026-07',
          title: t('Brands For Good Honouree, again'),
          text: t(
            'Named a Tech For Good Honouree for the second year running.',
          ),
        },
        {
          date: '2026-08',
          title: t('Top AI trust score in Asia'),
          text: t(
            'We scored 84/100 in the Cybernews AI Trustworthiness Ranking, the joint highest of 92 AI companies based in Asia.',
          ),
          href: SOURCES.cybernews,
          linkLabel: t('See the ranking'),
        },
      ],
      media: [
        {
          kind: 'photo',
          src: PHOTOS.brandsForGood2026,
          alt: t(
            'Marianna and Dmytro with the Tech For Good Honouree trophy at the Brands For Good Awards 2026',
          ),
          wide: true,
        },
        { kind: 'score' },
      ],
    },
  ];

  const products = [
    { name: t('Marky Agent'), href: '/marky' },
    { name: t('AI agents'), href: '/chatbots' },
    { name: t('Image generator'), href: '/ai-image-generator' },
    { name: t('Video generator'), href: '/ai-video-generator' },
    { name: t('Text to speech'), href: '/text-to-speech' },
    { name: t('300+ AI tools'), href: '/templates' },
  ];

  const press = [
    { name: 'e27', year: '2026', href: SOURCES.e27 },
    { name: 'Cybernews', year: '2026', href: SOURCES.cybernews },
    { name: 'ASCII.jp', year: '2023', href: SOURCES.ascii },
  ];

  return (
    <section id='story' className='scroll-mt-8 py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
          <SectionHeading
            className='lg:col-span-8'
            title={t('It started with three people.')}
            tail={t('Now millions use what we built.')}
            lead={t(
              'Marianna and Dmytro launched the first version in September 2022, with one goal: AI tools anyone can use, without code or complicated prompts. Today Easy-Peasy.AI brings Marky Agent, AI agents for businesses and the latest image, video and voice models together in one place.',
            )}
          />
          <nav
            aria-labelledby='about-products'
            className='lg:col-span-4 lg:pb-1.5'
          >
            <p
              id='about-products'
              className={clsxm('text-[13px] font-medium', tone.quiet)}
            >
              {t('What we make')}
            </p>
            <ul className='mt-3 flex flex-wrap gap-2'>
              {products.map(({ name, href }) => (
                <li key={href}>
                  <Link
                    href={href}
                    className={clsxm(
                      'inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-medium transition hover:ring-[#0f0f28]/25 dark:hover:ring-white/30',
                      tone.surface,
                      tone.line,
                      tone.ink,
                    )}
                  >
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <ol className='mt-16 sm:mt-20'>
          {chapters.map((chapter) => (
            <ChapterRow key={chapter.year} chapter={chapter} />
          ))}
        </ol>

        <div className='flex flex-col gap-4 border-t border-[#0f0f28]/[0.08] pt-10 sm:flex-row sm:items-center sm:gap-10 dark:border-white/[0.08]'>
          <p className={clsxm('text-sm font-medium', tone.quiet)}>
            {t('In the news')}
          </p>
          <ul className='flex flex-wrap gap-x-9 gap-y-3'>
            {press.map(({ name, year, href }) => (
              <li key={name}>
                <a
                  href={href}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={clsxm(
                    'group inline-flex items-baseline gap-2 text-xl font-semibold tracking-[-0.01em] transition-colors hover:text-primary-600 dark:hover:text-primary-400',
                    tone.ink,
                  )}
                >
                  {name}
                  <span className={clsxm('text-sm font-medium', tone.quiet)}>
                    {year}
                  </span>
                  <ArrowGlyph className='h-3.5 w-3.5 -rotate-45 self-center opacity-40 transition-opacity group-hover:opacity-100 rtl:rotate-45' />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function ChapterRow({ chapter }: { chapter: Chapter }) {
  const { t, lang } = useAboutTranslation();
  const { ref, state } = useReveal<HTMLLIElement>(0.15);
  const media = chapter.media ?? [];
  // Landscape photos and the score card sit two-up under the milestones;
  // square photos stay in a column beside them.
  const strip = media.some(
    (item) => item.kind === 'score' || item.wide === true,
  );
  // Next to the score card, a photo takes the card's height instead of 3:2.
  const hasScore = media.some((item) => item.kind === 'score');

  return (
    <li
      ref={ref}
      id={`year-${chapter.year}`}
      data-reveal={state}
      className={clsxm(
        styles.reveal,
        'grid scroll-mt-8 gap-8 border-t border-[#0f0f28]/[0.08] py-10 sm:py-12 lg:grid-cols-12 lg:gap-10 dark:border-white/[0.08]',
      )}
    >
      <div
        className={clsxm(styles.rv, 'lg:col-span-3', strip && 'lg:row-span-2')}
      >
        <p
          className={clsxm(
            styles.display,
            'text-5xl font-semibold leading-none tabular-nums text-primary-600 sm:text-6xl dark:text-primary-400',
          )}
        >
          {chapter.year}
        </p>
        <h3
          className={clsxm(
            'mt-3 text-lg font-semibold tracking-[-0.01em]',
            tone.ink,
          )}
        >
          {chapter.title}
        </h3>
      </div>

      <ol
        className={clsxm(
          styles.rv,
          'space-y-7',
          media.length && !strip
            ? 'lg:col-span-5'
            : 'lg:col-span-9 lg:max-w-2xl',
        )}
        style={delay(90)}
      >
        {chapter.milestones.map((milestone) => (
          <li
            key={milestone.title}
            className='grid grid-cols-[5.5rem_1fr] gap-x-4 sm:grid-cols-[6.5rem_1fr]'
          >
            <time
              dateTime={milestone.date}
              className={clsxm(
                'pt-0.5 text-sm font-medium tabular-nums',
                tone.quiet,
              )}
            >
              {formatMonth(milestone.date, lang)}
            </time>
            <div>
              <p
                className={clsxm(
                  'text-[17px] font-semibold leading-7 tracking-[-0.01em]',
                  tone.ink,
                )}
              >
                {milestone.title}
              </p>
              <p className={clsxm('mt-1 text-[15px] leading-7', tone.body)}>
                {milestone.text}
              </p>
              {milestone.href ? (
                <a
                  href={milestone.href}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 underline-offset-4 hover:underline dark:text-primary-400'
                >
                  {milestone.linkLabel ?? t('Read more')}
                  <ArrowGlyph className='h-3.5 w-3.5 -rotate-45 rtl:rotate-45' />
                </a>
              ) : null}
            </div>
          </li>
        ))}
      </ol>

      {media.length ? (
        <div
          className={clsxm(
            styles.rv,
            'grid gap-3',
            strip
              ? 'sm:grid-cols-2 lg:col-span-9 lg:col-start-4'
              : 'content-start lg:col-span-4',
            !strip && media.length > 1 && 'grid-cols-2',
          )}
          style={delay(180)}
        >
          {media.map((item) =>
            item.kind === 'score' ? (
              <ScoreCard key='score' />
            ) : (
              <figure
                key={item.src}
                className={clsxm(
                  'relative overflow-hidden rounded-[20px] bg-[#e8e8f0] dark:bg-white/5',
                  item.wide
                    ? clsxm('aspect-[3/2]', hasScore && 'sm:aspect-auto')
                    : 'aspect-square',
                )}
              >
                <Image
                  src={item.src}
                  alt={item.alt}
                  fill
                  unoptimized
                  className='object-cover'
                  style={
                    item.position
                      ? { objectPosition: item.position }
                      : undefined
                  }
                />
              </figure>
            ),
          )}
        </div>
      ) : null}
    </li>
  );
}

/** Our score next to the averages Cybernews published for the ranking. */
function ScoreCard() {
  const { t } = useAboutTranslation();

  const rows = [
    { label: 'Easy-Peasy.AI', value: 84, ours: true },
    { label: t('Average of 500 AI companies worldwide'), value: 65 },
    { label: t('Average of 92 AI companies in Asia'), value: 60 },
  ];

  return (
    <figure className='relative isolate overflow-hidden rounded-[20px] bg-[#0d0c1f] p-6 text-white ring-1 ring-inset ring-white/[0.07] sm:p-7'>
      <Halftone
        at='100% 0%'
        reach={60}
        className='-z-10 opacity-40 [--ht:rgb(var(--tw-color-primary-700))]'
      />
      <figcaption className='text-[13px] font-medium text-[#b7b7d2]'>
        {t('Cybernews AI Trustworthiness Ranking 2026')}
      </figcaption>
      <p dir='ltr' className='mt-3 flex items-baseline gap-1'>
        <span
          className={clsxm(
            styles.display,
            'text-6xl font-semibold leading-none tabular-nums',
          )}
        >
          84
        </span>
        <span className='text-xl font-medium text-[#9d9dc0]'>/100</span>
      </p>
      <p className='mt-2 text-sm text-[#b7b7d2]'>
        {t('The joint highest score in Asia')}
      </p>
      <dl className='mt-6 space-y-3.5'>
        {rows.map(({ label, value, ours }) => (
          <div key={label}>
            <div className='flex items-baseline justify-between gap-4 text-[13px]'>
              <dt className={ours ? 'font-semibold' : 'text-[#b7b7d2]'}>
                {label}
              </dt>
              <dd className='font-semibold tabular-nums'>{value}</dd>
            </div>
            <div
              aria-hidden='true'
              className='mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10'
            >
              <div
                className={clsxm(
                  'h-full rounded-full',
                  ours ? 'bg-primary-400' : 'bg-white/35',
                )}
                style={{ width: `${value}%` }}
              />
            </div>
          </div>
        ))}
      </dl>
    </figure>
  );
}
